/**
 * Browser-local, per-host opt-in that replaces the Save / Discard / Keep editing dialog with an
 * implicit Save for close and navigation actions. Reads and writes are synchronous and go to
 * `localStorage` directly on every call: no caching, no debounce, so a change made anywhere
 * (profile tab, dialog button, another tab of the same origin) is visible to the very next
 * host action. Storage failures degrade to "prompt".
 */
export type AutoSaveHostKind = 'card-description' | 'release-goal';

export const AUTO_SAVE_HOST_KINDS: readonly AutoSaveHostKind[] = ['card-description', 'release-goal'];

/** Text of the dialog button placed right of Save. */
export const autoSavePreferenceLabel = "Save and don't ask again";

export const autoSaveHostLabels: Record<AutoSaveHostKind, string> = {
    'card-description': 'Save card descriptions automatically when closing or navigating away',
    'release-goal': 'Save release goals automatically when closing or navigating away',
};

export const autoSavePreferenceKey = (kind: AutoSaveHostKind) => `_storiesonboard_autoSaveOnClose_${kind}`;

const storage = (): Storage | null => {
    try {
        return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
    } catch {
        return null;
    }
};

export function readAutoSavePreference(kind: AutoSaveHostKind): boolean {
    try {
        return storage()?.getItem(autoSavePreferenceKey(kind)) === 'true';
    } catch {
        return false;
    }
}

/** Returns whether the value was persisted. */
export function writeAutoSavePreference(kind: AutoSaveHostKind, value: boolean): boolean {
    try {
        const store = storage();
        if (!store) return false;
        if (value) store.setItem(autoSavePreferenceKey(kind), 'true');
        else store.removeItem(autoSavePreferenceKey(kind));
        return true;
    } catch {
        return false;
    }
}
